import React, { useState } from 'react';
import { styled } from '@mui/material';

import { useAuth } from 'contexts/auth';
import { useViewport } from 'contexts/viewport';
import { Avatar, AvatarGroup, IconButton, Link, ResponsiveDialog, Stack, Tag, Text, Tooltip } from 'shell/components';
import { getTooltipComponent, getUserPreferredFullName } from 'utils/helper';

const tooltipTexts = {
    onTrack: {
        title: 'Status: On Track',
        description: 'You’re doing great! Your team is making similar progress to other teams right now.',
    },
    caution: {
        title: 'Status: Caution',
        description: 'Keep it going! Your team is slightly behind other teams but you’ve got time to catch up.',
    },
    offTrack: {
        title: 'Status: Off Track',
        description:
            'Your team is behind other teams’ progress. Check in with your teammates to plan how to get back on track.',
    },
};

const TeamInformation = ({ groupName = '', courseName = '', members = [], instructors = [], status = '' }) => {
    const { auth, currentUser } = useAuth();
    const { device } = useViewport();

    const [memberVisible, setMemberVisible] = useState(false);
    const isIndividual = members?.length === 1;

    const Profile = (
        <Stack sx={{ minWidth: 0 }}>
            <Stack alignItems={'center'} direction={'row'} spacing={3}>
                <Avatar size={'medium'} user={currentUser} />
                <Stack>
                    <Text noWrap variant={'h6'} weight={'semibold'}>
                        {getUserPreferredFullName(currentUser)}{' '}
                    </Text>
                    <Text variant={'p2'}>{courseName}</Text>
                </Stack>
            </Stack>
        </Stack>
    );

    const Group = (
        <Stack sx={{ minWidth: 0 }}>
            <Text noWrap variant={'h6'} weight={'semibold'}>
                {groupName}
            </Text>
            <Text variant={'p2'}>{courseName}</Text>
        </Stack>
    );

    const Members = (
        <Stack
            alignItems={device === 'tablet' && auth?.role === 'instructor' ? 'center' : 'flex-start'}
            direction={device === 'tablet' && auth?.role === 'instructor' ? 'row' : 'column'}
            spacing={3}
        >
            <Text variant={'p3'}>Members</Text>
            <AvatarGroup sx={{ m: -2 }} max={6} size={'small'} onClick={() => setMemberVisible(true)}>
                {members.map((member, index) => (
                    <Avatar key={member.owner_account_id} sx={{ zIndex: index }} size={'small'} user={member} />
                ))}
            </AvatarGroup>
        </Stack>
    );

    const Instructors = (
        <Stack
            alignItems={device === 'tablet' && auth?.role === 'instructor' ? 'center' : 'flex-start'}
            direction={device === 'tablet' && auth?.role === 'instructor' ? 'row' : 'column'}
            spacing={3}
        >
            <Text variant={'p3'}>Instructors</Text>
            <AvatarGroup sx={{ m: -2 }} max={6} size={'small'} onClick={() => setMemberVisible(true)}>
                {instructors.map((instructor, index) => (
                    <Avatar key={instructor.owner_account_id} sx={{ zIndex: index }} size={'small'} user={instructor} />
                ))}
            </AvatarGroup>
        </Stack>
    );

    const Status = (
        <Stack
            alignItems={'center'}
            direction={device === 'tablet' && auth?.role === 'instructor' ? 'row' : 'column'}
            spacing={3}
        >
            <Stack alignItems={'center'} direction={'row'} spacing={2}>
                <Text variant={'p3'}>Status</Text>
                <Tooltip
                    content={
                        <Stack spacing={4}>
                            <Text color={'inherit'} variant={'p3'}>
                                <Text color={'inherit'} component={'span'} variant={'p3'} weight={'semibold'}>
                                    Your status
                                </Text>{' '}
                                is determined by your team’s current progress{' '}
                                <Text color={'inherit'} component={'span'} variant={'p3'} weight={'semibold'}>
                                    in relation to the class’ average progress
                                </Text>
                                .
                            </Text>
                            <Stack direction={'row'} spacing={1}>
                                <Text color={'inherit'} variant={'p3'}>
                                    Need help?
                                </Text>
                                <CustomLink
                                    external
                                    href={'https://www.youtube.com/watch?v=7GdEUIKzES0'}
                                    rel={'noopener noreferrer'}
                                    target={'_blank'}
                                >
                                    <CustomText variant={'p3'} weight={'semibold'}>
                                        Watch a tutorial here!
                                    </CustomText>
                                </CustomLink>
                            </Stack>
                        </Stack>
                    }
                    type={'info'}
                />
            </Stack>
            {status ? (
                <Tag
                    type={'progress'}
                    value={status}
                    tooltip={{
                        component: getTooltipComponent(tooltipTexts[status].title, tooltipTexts[status].description),
                    }}
                />
            ) : null}
        </Stack>
    );

    const renderInformation = () => {
        if (device === 'tablet' && auth?.role === 'student') {
            return (
                <Stack alignItems={'flex-start'} direction={'row'} justifyContent={'space-between'}>
                    {isIndividual && auth.role === 'student' ? Profile : Group}
                    <Stack alignItems={'center'} spacing={9} direction={'row'}>
                        {isIndividual && auth.role === 'student' ? Instructors : Members}
                        {Status}
                    </Stack>
                </Stack>
            );
        } else if (device === 'tablet' && auth?.role === 'instructor') {
            return (
                <>
                    {isIndividual && auth.role === 'student' ? Profile : Group}
                    <CustomTabletStack direction={'row'} justifyContent={'space-between'} spacing={2}>
                        {isIndividual && auth.role === 'student' ? Instructors : Members}
                        {Status}
                    </CustomTabletStack>
                </>
            );
        } else {
            return (
                <>
                    {isIndividual && auth.role === 'student' ? Profile : Group}
                    <Stack alignItems={'center'} direction={'row'} justifyContent={'flex-start'} spacing={16}>
                        {isIndividual && auth.role === 'student' ? Instructors : Members}
                        {Status}
                    </Stack>
                </>
            );
        }
    };

    return (
        <>
            <CustomStack sx={{ minWidth: 0, height: '100%' }} justifyContent={'center'} device={device} spacing={6}>
                {renderInformation()}
            </CustomStack>
            <ResponsiveDialog open={memberVisible} onClose={() => setMemberVisible(false)}>
                <Stack sx={{ minWidth: 250 }} spacing={4}>
                    <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                        <Text align={'left'} variant={'h6'} weight={'semibold'}>
                            {isIndividual && auth.role === 'student' ? 'Instructors' : 'Members'}
                        </Text>
                        <IconButton
                            aria-label={'close'}
                            kind={'exit'}
                            size={'medium'}
                            variant={'square'}
                            onClick={() => setMemberVisible(false)}
                        />
                    </Stack>
                    <Stack spacing={2}>
                        {(isIndividual && auth.role === 'student' ? instructors : members).map((member) => (
                            <Stack key={member.owner_account_id} alignItems={'center'} direction={'row'} spacing={2}>
                                <Avatar size={'small'} user={member} />
                                <Text variant={'p2'}>{getUserPreferredFullName(member)}</Text>
                            </Stack>
                        ))}
                    </Stack>
                </Stack>
            </ResponsiveDialog>
        </>
    );
};

const CustomStack = styled(Stack)(({ device, theme }) => {
    return {
        padding: theme.spacing(6, 11),
        background: theme.palette.ensightful.shades[0],
        ...(device === 'mobile' && {
            gap: theme.spacing(5),
            width: '100%',
            padding: theme.spacing(6),
        }),
    };
});

const CustomTabletStack = styled(Stack)(({}) => {
    return {
        flexWrap: 'wrap',
        width: '120px',
    };
});

const CustomLink = styled(Link)(({ theme }) => {
    return {
        textDecorationColor: theme.palette.ensightful.neutral[50],
        '&:hover': {
            textDecorationColor: theme.palette.ensightful.neutral[50],
        },
    };
});

const CustomText = styled(Text)(({ theme }) => {
    return {
        color: theme.palette.ensightful.neutral[50],
    };
});

export default TeamInformation;
